<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-indigo-200 rounded-lg shadow-lg border border-gray-700 overflow-hidden animate-scaleIn flex flex-col" style="max-height:90vh;">
      <!-- Header -->
      <div class="flex justify-between items-center p-4 border-b border-gray-700 bg-gray-800">
        <h2 class="text-lg font-semibold text-indigo-300">{{ map.name }}</h2>
        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group focus:outline-none"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>
      <!-- Tabs -->
      <div class="grid grid-cols-3 sm:grid-cols-6 border-b border-gray-700 bg-gray-700">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
          :class="activeTab === tab.id
            ? 'text-indigo-300 border-b-2 border-indigo-600'
            : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-600 border border-transparent'"
        >
          {{ tab.name }}
          <span v-if="activeTab === tab.id" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
        </button>
      </div>
      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
        <!-- Map Info Tab -->
        <div v-if="activeTab === 'mapInfo'" class="space-y-2 h-72 overflow-auto pb-20 animate-fadeIn" id="style-1">
          <!-- Basic Info -->
          <div class="bi-card">
            <div class="bi-header">
              <h3 class="bi-title">
                <i class="fas fa-info-circle"></i>
                ข้อมูลพื้นฐาน
              </h3>
            </div>
            <div class="bi-grid">
              <div class="bi-item"><i class="fas fa-user"></i><span>เลเวลขั้นต่ำ: {{ map.requiredLevel || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-bolt"></i><span>พลังงาน: {{ map.requiredEnergy || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-star"></i><span>ชื่อเสียง: {{ map.requiredRenown || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-frown"></i><span>ลดความสุข: {{ map.reduceHappy || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-ghost"></i><span>วิญญาณ: {{ map.minGhosts || 0 }}-{{ map.maxGhosts || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-running"></i><span>จำนวนครั้งที่หนีได้: {{ map.escapeAttempts || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-coins"></i><span>เหรียญสำหรับหนี: {{ map.escapeCoins || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-users"></i><span>โอกาสพบผู้เล่น: {{ map.meetPlayerChance || 0 }}%</span></div>
              <div class="bi-item"><i class="fas fa-door-closed"></i><span>จำนวนห้องที่ต้องการ: {{ map.roomRequired || 1 }}</span></div>
              <div class="bi-item bi-span2"><i class="fas fa-map"></i><span>แผนที่ที่ต้องมีก่อน: {{ map.requiredMap || 'ไม่มี' }}</span></div>
              <div class="bi-item bi-span2">
                <i class="fas fa-book"></i>
                <span>ทักษะที่ต้องมี: 
                  <span v-if="map.requiredSkills && map.requiredSkills.length > 0">
                    {{ map.requiredSkills.join(', ') }}
                  </span>
                  <span v-else>ไม่มี</span>
                </span>
              </div>
            </div>
          </div>
          <!-- Required Items -->
          <div v-if="map.requiredItems && map.requiredItems.length > 0" class="bi-card">
            <div class="bi-header">
              <h3 class="bi-title">
                <i class="fas fa-tools"></i>
                ไอเทมที่ต้องใช้ในการเข้าแผนที่
              </h3>
            </div>
            <div class="space-y-2">
              <div
                v-for="itemName in map.requiredItems"
                :key="itemName"
                class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
                @click="showItemDetails(itemName)"
              >
                <div class="relative">
                  <img
                    v-if="itemDetails[itemName]?.img"
                    :src="itemDetails[itemName].img"
                    :alt="itemName"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    @error="handleImageError"
                  />
                  <div v-if="isLoadingItem[itemName]" class="absolute inset-0 flex items-center justify-center">
                    <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                  </div>
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2 text-indigo-200 text-sm">
                    <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                      <img src="../assets/star.png" class="w-3 mr-1" />
                      <span>{{ itemDetails[itemName]?.rarity || 'N/A' }}</span>
                    </div>
                    <span>{{ itemName }}</span>
                  </div>
                  <p class="text-indigo-300 text-xs">ประเภท: {{ itemDetails[itemName]?.type || 'N/A' }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Entry Cost -->
          <div
            v-if="(map.requiredCoins || 0) + (map.requiredDiamonds || 0) + (map.requiredCash || 0) + (map.requiredGold || 0) >= 1"
            class="fee-card"
          >
            <div class="fee-header">
              <h3 class="fee-title">
                <i class="fas fa-door-open"></i>
                ค่าเข้า
              </h3>
            </div>
            <div class="fee-grid">
              <div v-if="map.requiredCoins >= 1" class="fee-chip">
                <i class="fas fa-coins text-gray-100"></i>
                <span>{{ map.requiredCoins }}</span>
              </div>
              <div v-if="map.requiredGold >= 1" class="fee-chip">
                <i class="fas fa-g fee-gold"></i>
                <span>{{ map.requiredGold }}</span>
              </div>
              <div v-if="map.requiredDiamonds >= 1" class="fee-chip">
                <i class="fas fa-gem fee-diamond"></i>
                <span>{{ map.requiredDiamonds }}</span>
              </div>
              <div v-if="map.requiredCash >= 1" class="fee-chip">
                <i class="fas fa-money-bill fee-cash"></i>
                <span>{{ map.requiredCash }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Ghosts Tab -->
        <div v-if="activeTab === 'ghosts'" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
          <h3 class="text-sm font-semibold text-indigo-300">วิญญาณในแผนที่</h3>
          <div v-if="sortedGhosts.length > 0" class="space-y-2">
            <div
              v-for="ghost in sortedGhosts"
              :key="ghost.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showGhostDetails(ghost.name)"
            >
              <div class="relative">
                <img
                  v-if="ghostDetails[ghost.name]?.imageUrl"
                  :src="ghostDetails[ghost.name].imageUrl"
                  :alt="ghost.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingGhost[ghost.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <p class="text-indigo-200 font-medium text-sm">{{ ghost.name }}</p>
                <p class="text-indigo-300 text-xs">โอกาส: {{ ghost.chance }}%</p>
                <p class="text-indigo-300 text-xs">เลเวล: {{ ghostDetails[ghost.name]?.level || 'N/A' }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลวิญญาณ</p>
        </div>
        <!-- Boss Ghosts Tab -->
        <div v-if="activeTab === 'bossGhosts'" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
          <h3 class="text-sm font-semibold text-indigo-300">บอสวิญญาณในแผนที่</h3>
          <div v-if="sortedBossGhosts.length > 0" class="space-y-2">
            <div
              v-for="bossGhost in sortedBossGhosts"
              :key="bossGhost.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showBossGhostDetails(bossGhost.name)"
            >
              <div class="relative">
                <img
                  v-if="bossGhostDetails[bossGhost.name]?.imageUrl"
                  :src="bossGhostDetails[bossGhost.name].imageUrl"
                  :alt="bossGhost.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingBossGhost[bossGhost.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <p class="text-indigo-200 font-medium text-sm">{{ bossGhost.name }}</p>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (bossGhost.chance / 100).toFixed(2) }}%</p>
                <p class="text-indigo-300 text-xs">เลเวล: {{ bossGhostDetails[bossGhost.name]?.level || 'N/A' }}</p>
                <p
                  :class="{
                    'text-red-500': !bossGhostStatus[bossGhost.name]?.isAlive && bossGhostStatus[bossGhost.name]?.nextRespawn && bossGhostStatus[bossGhost.name]?.nextRespawn > Date.now(),
                    'text-green-500': bossGhostStatus[bossGhost.name]?.isAlive || (bossGhostStatus[bossGhost.name]?.nextRespawn && bossGhostStatus[bossGhost.name]?.nextRespawn <= Date.now())
                  }"
                  class="text-xs"
                >
                  สถานะ: 
                  <span v-if="bossGhostStatus[bossGhost.name]?.isAlive">เกิดแล้ว</span>
                  <span v-else-if="bossGhostStatus[bossGhost.name]?.nextRespawn && bossGhostStatus[bossGhost.name]?.nextRespawn <= Date.now()">เกิดแล้ว</span>
                  <span v-else>ยังไม่เกิด กรุณารอ: {{ bossGhostStatus[bossGhost.name]?.respawnTime || 'N/A' }}</span>
                </p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลบอสวิญญาณ</p>
        </div>
        <!-- Merchants Tab -->
        <div v-if="activeTab === 'merchants'" class="space-y-3 animate-fadeIn">
          <h3 class="text-sm font-semibold text-indigo-300">พ่อค้าเร่ในแผนที่</h3>
          <div v-if="sortedMerchants.length > 0" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
            <div
              v-for="merchant in sortedMerchants"
              :key="merchant.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showMerchantDetails(merchant.name)"
            >
              <div class="relative">
                <img
                  v-if="merchantDetails[merchant.name]?.img"
                  :src="merchantDetails[merchant.name].img"
                  :alt="merchant.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingMerchant[merchant.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 text-indigo-200 text-sm">
                  <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                    <img src="../assets/star.png" class="w-3 mr-1" />
                    <span>{{ merchantDetails[merchant.name]?.rarity || 'N/A' }}</span>
                  </div>
                  <span>{{ merchant.name }}</span>
                </div>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (merchant.chance / 100).toFixed(2) }}%</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลพ่อค้าเร่</p>
        </div>
        <!-- Rewards Tab -->
        <div v-if="activeTab === 'rewards'" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
          <div class="bg-gray-800 rounded-md p-4 border border-gray-700">
            <h4 class="text-xs sm:text-sm font-semibold text-indigo-300 mb-2">รางวัลเพชร</h4>
            <div class="flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm text-indigo-200 gap-1">
              <span>ขั้นต่ำ: {{ map.diamondDropMin || 0 }}</span>
              <span>ขั้นสูงสุด: {{ map.diamondDropMax || 0 }}</span>
              <span>โอกาสได้รับ: {{ map.diamondDropChance || 0 }}%</span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <label for="itemTypeFilter" class="text-indigo-200 text-xs sm:text-sm">ประเภทไอเทม:</label>
            <select
              id="itemTypeFilter"
              v-model="selectedItemType"
              class="bg-gray-800 text-indigo-200 border border-gray-600 rounded-md px-2 py-1 text-xs sm:text-sm focus:outline-none"
            >
              <option value="">ทั้งหมด</option>
              <option value="wearable">ไอเทมสวมใส่</option>
              <option value="book">หนังสือ</option>
              <option value="spirit_material">วัสดุวิญญาณ</option>
              <option value="general">ทั่วไป</option>
              <option value="food">อาหาร</option>
              <option value="map">แผนที่</option>
              <option value="heal">รักษา</option>
              <option value="mechanical_organ">อวัยวะจักรกล</option>
            </select>
          </div>
          <div v-if="filteredRewards.length > 0" class="space-y-2">
            <div
              v-for="reward in filteredRewards"
              :key="reward.itemName"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showItemDetails(reward.itemName)"
            >
              <div class="relative">
                <img
                  v-if="itemDetails[reward.itemName]?.img"
                  :src="itemDetails[reward.itemName].img"
                  :alt="reward.itemName"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  :class="[reward.rarityColors?.borderColor || 'border-gray-600']"
                  @error="handleImageError"
                />
                <div v-if="isLoadingItem[reward.itemName]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 text-indigo-200 text-sm">
                  <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                    <img src="../assets/star.png" class="w-3 mr-1" />
                    <span>{{ itemDetails[reward.itemName]?.rarity || 'N/A' }}</span>
                  </div>
                  <span>{{ reward.itemName }}</span>
                </div>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (reward.dropChance / 100).toFixed(2) }}%</p>
                <p class="text-indigo-300 text-xs">ประเภท: {{ itemDetails[reward.itemName]?.type || 'N/A' }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลรางวัล</p>
        </div>
        <!-- Situations Tab -->
        <div v-if="activeTab === 'situations'" class="space-y-3 h-72 overflow-auto pb-20" id="style-1">
          <h3 class="text-sm font-semibold text-indigo-300">สถานการณ์ในแผนที่</h3>
          <div v-if="sortedSituations.length > 0" class="space-y-2">
            <div
              v-for="situation in sortedSituations"
              :key="situation.name"
              class="p-3 bg-gray-800 rounded-md border border-gray-600"
            >
              <div class="flex items-center gap-3 mb-2">
                <div class="flex-1">
                  <p class="text-indigo-200 font-medium text-sm">{{ situation.name }}</p>
                  <p class="text-indigo-300 text-xs">โอกาส: {{ situation.chance }}%</p>
                  <p class="text-indigo-300 text-xs">วิญญาณ: {{ situation.minGhosts }}-{{ situation.maxGhosts }}</p>
                </div>
              </div>
              <div v-if="situation.rewards && situation.rewards.length > 0" class="space-y-2">
                <p class="text-indigo-300 text-xs font-medium">รางวัล:</p>
                <div
                  v-for="reward in situation.rewards"
                  :key="reward.itemName"
                  class="flex items-center gap-3 p-2 bg-gray-700 rounded-md cursor-pointer hover:bg-gray-600 transition-all duration-200 border border-gray-600"
                  @click="showItemDetails(reward.itemName)"
                >
                  <div class="relative">
                    <img
                      v-if="itemDetails[reward.itemName]?.img"
                      :src="itemDetails[reward.itemName].img"
                      :alt="reward.itemName"
                      class="w-8 h-8 object-cover rounded-full border border-gray-600"
                      :class="[reward.rarityColors?.borderColor || 'border-gray-600']"
                      @error="handleImageError"
                    />
                    <div v-if="isLoadingItem[reward.itemName]" class="absolute inset-0 flex items-center justify-center">
                      <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2 text-indigo-200 text-xs">
                      <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                        <img src="../assets/star.png" class="w-3 mr-1" />
                        <span>{{ itemDetails[reward.itemName]?.rarity || 'N/A' }}</span>
                      </div>
                      <span>{{ reward.itemName }}</span>
                    </div>
                    <p class="text-indigo-300 text-xs">โอกาส: {{ (reward.dropChance / 100).toFixed(2) }}%</p>
                    <p class="text-indigo-300 text-xs">ประเภท: {{ itemDetails[reward.itemName]?.type || 'N/A' }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="text-indigo-300 text-xs">ไม่มีรางวัลสำหรับสถานการณ์นี้</p>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลสถานการณ์</p>
        </div>
        <!-- Ghost Details Modal -->
        <div
          v-if="selectedGhost"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedGhost.name }}</h3>
              <button @click="selectedGhost = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="ghostDetails[selectedGhost.name]?.imageUrl"
                  :src="ghostDetails[selectedGhost.name].imageUrl"
                  :alt="selectedGhost.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="ghostDetails[selectedGhost.name]?.level">
                  <span class="font-medium">เลเวล:</span> {{ ghostDetails[selectedGhost.name]?.level || 'N/A' }}
                </div>
              </div>
              <div v-if="ghostDetails[selectedGhost.name]?.description && ghostDetails[selectedGhost.name]?.description !== 'ไม่มี'">
                <span class="font-medium">คำอธิบาย:</span> {{ ghostDetails[selectedGhost.name]?.description }}
              </div>
              <div v-if="hasNonZeroStats(ghostDetails[selectedGhost.name]?.stats)">
                <span class="font-medium">Stats:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.stamina > 0">Stamina: {{ ghostDetails[selectedGhost.name]?.stats?.stamina }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.str > 0">Strength: {{ ghostDetails[selectedGhost.name]?.stats?.str }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.int > 0">Intelligence: {{ ghostDetails[selectedGhost.name]?.stats?.int }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.agi > 0">Agility: {{ ghostDetails[selectedGhost.name]?.stats?.agi }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.luck > 0">Luck: {{ ghostDetails[selectedGhost.name]?.stats?.luck }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.maxHp > 0">Max HP: {{ ghostDetails[selectedGhost.name]?.stats?.maxHp }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.atk > 0">Attack: {{ ghostDetails[selectedGhost.name]?.stats?.atk }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.evasion > 0">Evasion: {{ ghostDetails[selectedGhost.name]?.stats?.evasion }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.critical > 0">Critical: {{ ghostDetails[selectedGhost.name]?.stats?.critical }}</li>
                  <li v-if="ghostDetails[selectedGhost.name]?.stats?.defense > 0">Defense: {{ ghostDetails[selectedGhost.name]?.stats?.defense }}</li>
                </ul>
              </div>
              <div v-if="ghostDetails[selectedGhost.name]?.captureSkills?.length > 0">
                <span class="font-medium">ทักษะที่ต้องมีในการจับ:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-for="(skill, index) in ghostDetails[selectedGhost.name]?.captureSkills" :key="index">{{ skill }}</li>
                </ul>
              </div>
              <div v-if="ghostDetails[selectedGhost.name]?.decomposeItems?.length > 0">
                <span class="font-medium">ไอเทมจากการสลายวิญญาณ:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-for="(decompose, index) in ghostDetails[selectedGhost.name]?.decomposeItems" :key="index">
                    {{ decompose.itemName }}: {{ decompose.chance.toFixed(2) }}%
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <!-- Boss Ghost Details Modal -->
        <div
          v-if="selectedBossGhost"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedBossGhost.name }}</h3>
              <button @click="selectedBossGhost = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="bossGhostDetails[selectedBossGhost.name]?.imageUrl"
                  :src="bossGhostDetails[selectedBossGhost.name].imageUrl"
                  :alt="selectedBossGhost.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="bossGhostDetails[selectedBossGhost.name]?.level">
                  <span class="font-medium">เลเวล:</span> {{ bossGhostDetails[selectedBossGhost.name]?.level || 'N/A' }}
                </div>
              </div>
              <div v-if="bossGhostDetails[selectedBossGhost.name]?.description && bossGhostDetails[selectedBossGhost.name]?.description !== 'ไม่มี'">
                <span class="font-medium">คำอธิบาย:</span> {{ bossGhostDetails[selectedBossGhost.name]?.description }}
              </div>
              <div v-if="hasNonZeroStats(bossGhostDetails[selectedBossGhost.name]?.stats)">
                <span class="font-medium">Stats:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.stamina > 0">Stamina: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.stamina }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.str > 0">Strength: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.str }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.int > 0">Intelligence: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.int }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.agi > 0">Agility: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.agi }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.luck > 0">Luck: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.luck }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.maxHp > 0">Max HP: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.maxHp }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.atk > 0">Attack: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.atk }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.evasion > 0">Evasion: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.evasion }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.critical > 0">Critical: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.critical }}</li>
                  <li v-if="bossGhostDetails[selectedBossGhost.name]?.stats?.defense > 0">Defense: {{ bossGhostDetails[selectedBossGhost.name]?.stats?.defense }}</li>
                </ul>
              </div>
              <div v-if="bossGhostDetails[selectedBossGhost.name]?.captureSkills?.length > 0">
                <span class="font-medium">ทักษะที่ต้องมีในการจับ:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-for="(skill, index) in bossGhostDetails[selectedBossGhost.name]?.captureSkills" :key="index">{{ skill }}</li>
                </ul>
              </div>
              <div v-if="bossGhostDetails[selectedBossGhost.name]?.decomposeItems?.length > 0">
                <span class="font-medium">ไอเทมจากการสลายวิญญาณ:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-for="(decompose, index) in bossGhostDetails[selectedBossGhost.name]?.decomposeItems" :key="index">
                    {{ decompose.itemName }}: {{ decompose.chance.toFixed(2) }}%
                  </li>
                </ul>
              </div>
              <div v-if="bossGhostDetails[selectedBossGhost.name]?.drops?.length > 0">
                <span class="font-medium">ไอเทมที่ดรอป:</span>
                <div class="space-y-2 mt-2">
                  <div
                    v-for="drop in bossGhostDetails[selectedBossGhost.name]?.drops"
                    :key="drop.itemName"
                    class="flex items-center gap-3 p-2 bg-gray-700 rounded-md cursor-pointer hover:bg-gray-600 transition-all duration-200 border border-gray-600"
                    @click="showItemDetails(drop.itemName)"
                  >
                    <div class="relative">
                      <img
                        v-if="itemDetails[drop.itemName]?.img"
                        :src="itemDetails[drop.itemName].img"
                        :alt="drop.itemName"
                        class="w-8 h-8 object-cover rounded-full border border-gray-600"
                        @error="handleImageError"
                      />
                      <div v-if="isLoadingItem[drop.itemName]" class="absolute inset-0 flex items-center justify-center">
                        <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                      </div>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2 text-indigo-200 text-xs">
                        <span>{{ drop.itemName }}</span>
                      </div>
                      <p class="text-indigo-300 text-xs">โอกาส: {{ formatDropChance(drop.dropChance) }}%</p>
                      <p class="text-indigo-300 text-xs">ประเภท: {{ itemDetails[drop.itemName]?.type || 'N/A' }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else class="text-indigo-300 text-xs">ไม่มีไอเทมที่ดรอป</p>
            </div>
          </div>
        </div>
        <!-- Merchant Details Modal -->
        <div
          v-if="selectedMerchant"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedMerchant.name }}</h3>
              <button @click="selectedMerchant = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="merchantDetails[selectedMerchant.name]?.img"
                  :src="merchantDetails[selectedMerchant.name].img"
                  :alt="selectedMerchant.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="merchantDetails[selectedMerchant.name]?.rarity > 0"><span class="font-medium">ระดับความหายาก:</span> {{ merchantDetails[selectedMerchant.name]?.rarity }}</div>
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.items?.length > 0">
                <span class="font-medium">สินค้าที่ขาย:</span>
                <ul class="list-disc ml-4 text-xs space-y-2">
                  <li v-for="(item, index) in merchantDetails[selectedMerchant.name]?.items" :key="index" class="flex items-center gap-2">
                    <img
                      v-if="itemDetails[item.name]?.img"
                      :src="itemDetails[item.name].img"
                      :alt="item.name"
                      class="w-6 h-6 object-cover rounded-full border border-gray-600"
                      @error="handleImageError"
                    />
                    <span>{{ item.name }}: {{ item.price }}</span>
                    <i v-if="item.currency === 'diamond'" class="fas fa-gem text-blue-400"></i>
                    <i v-else-if="item.currency === 'gold'" class="fas fa-coins text-yellow-400"></i>
                    <i v-else class="fas fa-coins text-gray-200"></i>
                  </li>
                </ul>
              </div>
              <div>
                <span class="font-medium">อนุญาตให้ต่อสู้เพื่อปล้น:</span> {{ merchantDetails[selectedMerchant.name]?.allowRob ? 'ใช่' : 'ไม่' }}
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.allowRob">
                <span class="font-medium">รางวัลจากการปล้น:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li>Silver: {{ merchantDetails[selectedMerchant.name]?.robRewards?.silver || 0 }}</li>
                  <li>Gold: {{ merchantDetails[selectedMerchant.name]?.robRewards?.gold || 0 }}</li>
                  <li>Diamond: {{ merchantDetails[selectedMerchant.name]?.robRewards?.diamond || 0 }}</li>
                </ul>
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.normalFace">
                <span class="font-medium">รูปหน้าปกติ:</span>
                <img :src="merchantDetails[selectedMerchant.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2 border border-gray-600" @error="handleImageError" />
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.angryFace">
                <span class="font-medium">รูปหน้าที่โมโห:</span>
                <img :src="merchantDetails[selectedMerchant.name].angryFace" alt="Angry Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2 border border-gray-600" @error="handleImageError" />
              </div>
              <div class="space-y-1">
                <span class="font-medium">ค่าสถานะ:</span>
                <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  <div class="flex items-center space-x-1"><i class="fas fa-heart text-red-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.stamina || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-dumbbell text-yellow-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.str || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-brain text-blue-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.int || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-running text-green-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.agi || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-clover text-green-300"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.luck || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-shield-alt text-purple-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.defense || 0 }}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Item Details Modal -->
        <div
          v-if="selectedItem"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedItem.itemName }}</h3>
              <button @click="selectedItem = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิดหน้าต่างรายละเอียดไอเทม">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="itemDetails[selectedItem.itemName]?.img"
                  :src="itemDetails[selectedItem.itemName].img"
                  :alt="selectedItem.itemName"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  :class="[selectedItem.rarityColors?.borderColor || 'border-gray-600']"
                  @error="handleImageError"
                />
                <div>
                  <div v-if="itemDetails[selectedItem.itemName]?.type"><span class="font-medium">ประเภท:</span> {{ itemDetails[selectedItem.itemName]?.type || 'N/A' }}</div>
                  <div v-if="itemDetails[selectedItem.itemName]?.rarity"><span class="font-medium">ความหายาก:</span> {{ itemDetails[selectedItem.itemName]?.rarity || 'N/A' }}</div>
                </div>
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.requiredLevel > 0">
                <span class="font-medium">เลเวลขั้นต่ำ:</span> {{ itemDetails[selectedItem.itemName]?.requiredLevel }}
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.weight > 0">
                <span class="font-medium">น้ำหนัก:</span> {{ itemDetails[selectedItem.itemName]?.weight }}
              </div>
              <div>
                <span class="font-medium">สวมใส่ได้:</span> {{ wearableTypes.includes(itemDetails[selectedItem.itemName]?.type) ? 'ใช่' : 'ไม่' }}
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.price > 0">
                <span class="font-medium">ราคา:</span> {{ itemDetails[selectedItem.itemName]?.price }}
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.description && itemDetails[selectedItem.itemName]?.description !== 'ไม่มี'">
                <span class="font-medium">คำอธิบาย:</span> {{ itemDetails[selectedItem.itemName]?.description }}
              </div>
              <div v-if="hasNonZeroStats(itemDetails[selectedItem.itemName]?.stats)">
                <span class="font-medium">Stats:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.stamina > 0">Stamina: {{ itemDetails[selectedItem.itemName]?.stats?.stamina }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.str > 0">Strength: {{ itemDetails[selectedItem.itemName]?.stats?.str }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.int > 0">Intelligence: {{ itemDetails[selectedItem.itemName]?.stats?.int }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.agi > 0">Agility: {{ itemDetails[selectedItem.itemName]?.stats?.agi }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.luck > 0">Luck: {{ itemDetails[selectedItem.itemName]?.stats?.luck }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.maxHp > 0">Max HP: {{ itemDetails[selectedItem.itemName]?.stats?.maxHp }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.atk > 0">Attack: {{ itemDetails[selectedItem.itemName]?.stats?.atk }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.evasion > 0">Evasion: {{ itemDetails[selectedItem.itemName]?.stats?.evasion }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.critical > 0">Critical: {{ itemDetails[selectedItem.itemName]?.stats?.critical }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.defense > 0">Defense: {{ itemDetails[selectedItem.itemName]?.stats?.defense }}</li>
                </ul>
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.type === 'รักษา' && itemDetails[selectedItem.itemName]?.healHpPercentage > 0">
                <span class="font-medium">HP ที่ได้รับ:</span> {{ itemDetails[selectedItem.itemName]?.healHpPercentage }}%
              </div>
              <div>
                <span class="font-medium">แยกส่วนได้:</span> {{ itemDetails[selectedItem.itemName]?.decomposable === true ? 'ใช่' : 'ไม่' }}
                <span v-if="itemDetails[selectedItem.itemName]?.decomposable === 'true'">
                  (ไปยัง: {{ itemDetails[selectedItem.itemName]?.decomposeTo || 'N/A' }}, โอกาส: {{ itemDetails[selectedItem.itemName]?.decomposeChance || 0 }}%)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed, onUnmounted } from 'vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: {
    map: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    // Tabs & base states
    const activeTab = ref('mapInfo');
    const tabs = computed(() => {
      const baseTabs = [
        { id: 'mapInfo', name: 'ข้อมูลแผนที่', tooltip: 'ดูข้อมูลพื้นฐานของแผนที่' },
        { id: 'ghosts', name: 'วิญญาณ', tooltip: 'ดูวิญญาณในแผนที่' },
        { id: 'bossGhosts', name: 'บอสวิญญาณ', tooltip: 'ดูบอสวิญญาณในแผนที่' },
        { id: 'merchants', name: 'พ่อค้าเร่', tooltip: 'ดูพ่อค้าเร่ในแผนที่' },
        { id: 'rewards', name: 'รางวัล', tooltip: 'ดูรางวัลที่ได้รับจากแผนที่' },
        { id: 'situations', name: 'สถานการณ์', tooltip: 'ดูสถานการณ์ที่อาจเกิดขึ้นในแผนที่' },
      ];
      return baseTabs.filter(tab =>
        (tab.id !== 'ghosts' || (Array.isArray(props.map.ghosts) && props.map.ghosts.length > 0)) &&
        (tab.id !== 'bossGhosts' || (Array.isArray(props.map.bossGhosts) && props.map.bossGhosts.length > 0)) &&
        (tab.id !== 'merchants' || (Array.isArray(props.map.merchants) && props.map.merchants.length > 0)) &&
        (tab.id !== 'situations' || (Array.isArray(props.map.situations) && props.map.situations.length > 0))
      );
    });

    // Data caches
    const ghostDetails = ref({});
    const bossGhostDetails = ref({});
    const merchantDetails = ref({});
    const itemDetails = ref({});
    const bossGhostStatus = ref({});

    // Loading state maps
    const isLoadingGhost = ref({});
    const isLoadingBossGhost = ref({});
    const isLoadingMerchant = ref({});
    const isLoadingItem = ref({});

    // Selections
    const selectedGhost = ref(null);
    const selectedBossGhost = ref(null);
    const selectedMerchant = ref(null);
    const selectedItem = ref(null);
    const selectedItemType = ref('');

    // User
    const { user } = getUser();

    // Wearable types
    const wearableTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
    ];

    // Sorters / filters
    const sortedGhosts = computed(() => [...(props.map.ghosts || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedBossGhosts = computed(() => [...(props.map.bossGhosts || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedMerchants = computed(() => [...(props.map.merchants || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedRewards = computed(() => [...(props.map.rewards || [])].sort((a, b) => (b.dropChance || 0) - (a.chance || 0)));
    const sortedSituations = computed(() => [...(props.map.situations || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const filteredRewards = computed(() => {
      if (!selectedItemType.value) return sortedRewards.value;
      return sortedRewards.value.filter(reward => {
        const item = itemDetails.value[reward.itemName];
        if (!item) return false;
        if (selectedItemType.value === 'spirit_material') return item.type === 'วัสดุวิญญาณ';
        if (selectedItemType.value === 'wearable') return wearableTypes.includes(item.type);
        if (selectedItemType.value === 'book') return item.type === 'หนังสือ';
        if (selectedItemType.value === 'general') return item.type === 'ทั่วไป';
        if (selectedItemType.value === 'food') return item.type === 'อาหาร';
        if (selectedItemType.value === 'map') return item.type === 'แผนที่';
        if (selectedItemType.value === 'heal') return item.type === 'รักษา';
        if (selectedItemType.value === 'mechanical_organ') return item.type === 'อวัยวะจักรกล';
        return false;
      });
    });

    // Helpers
    const hasNonZeroStats = (stats) => stats && Object.values(stats).some(v => v > 0);

    const formatDropChance = (dropChance) => {
      if (typeof dropChance !== 'number' || isNaN(dropChance)) {
        return 'N/A';
      }
      return (dropChance / 100).toFixed(2);
    };

    // Function to format countdown
    const formatCountdown = (diff) => {
      if (diff <= 0) return 'พร้อมรีสปาวน์';
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      return `${hours} ชั่วโมง ${minutes} นาที ${seconds} วินาที`;
    };

    // Firestore fetchers
    const fetchGhostDetails = async (ghostName) => {
      if (!ghostDetails.value[ghostName] && !isLoadingGhost.value[ghostName]) {
        try {
          isLoadingGhost.value[ghostName] = true;
          const doc = await projectFirestore.collection('gameGhosts').doc(ghostName).get();
          ghostDetails.value[ghostName] = doc.exists ? doc.data() : null;
        } catch (e) {
          console.error(`Error fetching ghost ${ghostName}:`, e);
          ghostDetails.value[ghostName] = null;
        } finally {
          isLoadingGhost.value[ghostName] = false;
        }
      }
    };

    const fetchBossGhostDetails = async (bossGhostName) => {
      if (!bossGhostDetails.value[bossGhostName] && !isLoadingBossGhost.value[bossGhostName]) {
        try {
          isLoadingBossGhost.value[bossGhostName] = true;
          const doc = await projectFirestore.collection('gameGhosts').doc(bossGhostName).get();
          const data = doc.exists ? doc.data() : null;
          bossGhostDetails.value[bossGhostName] = data;
          // Fetch item details for drops
          if (data?.drops?.length > 0) {
            data.drops.forEach(drop => fetchItemDetails(drop.itemName));
          }
        } catch (e) {
          console.error(`Error fetching boss ghost ${bossGhostName}:`, e);
          bossGhostDetails.value[bossGhostName] = null;
        } finally {
          isLoadingBossGhost.value[bossGhostName] = false;
        }
      }
    };

    const fetchMerchantDetails = async (merchantName) => {
      if (!merchantDetails.value[merchantName] && !isLoadingMerchant.value[merchantName]) {
        try {
          isLoadingMerchant.value[merchantName] = true;
          const doc = await projectFirestore.collection('gameMerchants').doc(merchantName).get();
          merchantDetails.value[merchantName] = doc.exists ? doc.data() : null;
        } catch (e) {
          console.error(`Error fetching merchant ${merchantName}:`, e);
          merchantDetails.value[merchantName] = null;
        } finally {
          isLoadingMerchant.value[merchantName] = false;
        }
      }
    };

    const fetchItemDetails = (itemName) => {
      if (!itemDetails.value[itemName] && !isLoadingItem.value[itemName]) {
        try {
          isLoadingItem.value[itemName] = true;
          itemDetails.value[itemName] = gameItemsData[itemName] || null;
        } catch (e) {
          console.error(`Error fetching item ${itemName}:`, e);
          itemDetails.value[itemName] = null;
        } finally {
          isLoadingItem.value[itemName] = false;
        }
      }
    };

    const fetchBossGhostStatus = async (bossGhostName) => {
      if (!bossGhostStatus.value[bossGhostName]) {
        try {
          const doc = await projectFirestore.collection('bossGhostStatus').doc(`${props.map.name}_${bossGhostName}`).get();
          if (doc.exists) {
            const data = doc.data();
            const isAlive = data.isAlive || false;
            let nextRespawn = data.nextRespawn ? data.nextRespawn.toMillis() : null;
            let respawnTime = 'N/A';
            if (!isAlive && nextRespawn) {
              const diff = nextRespawn - Date.now();
              respawnTime = formatCountdown(diff);
            }
            bossGhostStatus.value[bossGhostName] = { isAlive, nextRespawn, respawnTime };
          } else {
            bossGhostStatus.value[bossGhostName] = { isAlive: true, nextRespawn: null, respawnTime: 'N/A' };
          }
        } catch (e) {
          console.error(`Error fetching boss ghost status ${bossGhostName}:`, e);
          bossGhostStatus.value[bossGhostName] = { isAlive: true, nextRespawn: null, respawnTime: 'N/A' };
        }
      }
    };

    const handleImageError = (e) => {
      e.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };

    // Show detail modals
    const showGhostDetails = async (ghostName) => {
      await fetchGhostDetails(ghostName);
      const ghost = (props.map.ghosts || []).find(g => g.name === ghostName);
      if (ghost && ghostDetails.value[ghostName]) {
        selectedGhost.value = ghost;
      }
    };

    const showBossGhostDetails = async (bossGhostName) => {
      await fetchBossGhostDetails(bossGhostName);
      await fetchBossGhostStatus(bossGhostName);
      const bossGhost = (props.map.bossGhosts || []).find(bg => bg.name === bossGhostName);
      if (bossGhost && bossGhostDetails.value[bossGhostName]) {
        selectedBossGhost.value = bossGhost;
      }
    };

    const showMerchantDetails = async (merchantName) => {
      await fetchMerchantDetails(merchantName);
      const merchant = (props.map.merchants || []).find(m => m.name === merchantName);
      if (merchant && merchantDetails.value[merchantName]) {
        if (merchantDetails.value[merchantName]?.items?.length > 0) {
          merchantDetails.value[merchantName].items.forEach(item => fetchItemDetails(item.name));
        }
        selectedMerchant.value = merchant;
      }
    };

    const showItemDetails = async (itemName) => {
      fetchItemDetails(itemName);
      const item = (props.map.rewards || []).find(r => r.itemName === itemName) ||
                   (props.map.situations || []).flatMap(s => s.rewards || []).find(r => r.itemName === itemName) ||
                   { itemName }; // For requiredItems or drops, no dropChance
      if (item && itemDetails.value[itemName]) {
        selectedItem.value = item;
      }
    };

    // React to map change
    watch(() => props.map, async (newMap) => {
      if (!newMap) return;
      // Reset caches
      ghostDetails.value = {};
      bossGhostDetails.value = {};
      merchantDetails.value = {};
      itemDetails.value = {};
      isLoadingGhost.value = {};
      isLoadingBossGhost.value = {};
      isLoadingMerchant.value = {};
      isLoadingItem.value = {};
      bossGhostStatus.value = {};
      // Prefetch
      await Promise.all((newMap.ghosts || []).map(g => fetchGhostDetails(g.name)));
      await Promise.all((newMap.bossGhosts || []).map(bg => Promise.all([
        fetchBossGhostDetails(bg.name),
        fetchBossGhostStatus(bg.name)
      ])));
      await Promise.all((newMap.merchants || []).map(m => fetchMerchantDetails(m.name)));
      (newMap.rewards || []).forEach(r => fetchItemDetails(r.itemName));
      (newMap.requiredItems || []).forEach(itemName => fetchItemDetails(itemName));
      if (Array.isArray(newMap.situations)) {
        newMap.situations.forEach(s => (s.rewards || []).forEach(r => fetchItemDetails(r.itemName)));
      }
    }, { immediate: true });

    // Update boss ghost status countdowns in real-time
    const bossStatusInterval = setInterval(() => {
      if (props.map.bossGhosts) {
        props.map.bossGhosts.forEach(bg => {
          const status = bossGhostStatus.value[bg.name];
          if (status && !status.isAlive && status.nextRespawn) {
            const diff = status.nextRespawn - Date.now();
            status.respawnTime = formatCountdown(diff);
            bossGhostStatus.value[bg.name] = { ...status };
          }
        });
      }
    }, 1000);

    onUnmounted(() => {
      if (bossStatusInterval) clearInterval(bossStatusInterval);
    });

    return {
      activeTab,
      tabs,
      ghostDetails,
      bossGhostDetails,
      merchantDetails,
      itemDetails,
      bossGhostStatus,
      isLoadingGhost,
      isLoadingBossGhost,
      isLoadingMerchant,
      isLoadingItem,
      sortedGhosts,
      sortedBossGhosts,
      sortedMerchants,
      sortedRewards,
      sortedSituations,
      filteredRewards,
      selectedGhost,
      selectedBossGhost,
      selectedMerchant,
      selectedItem,
      selectedItemType,
      wearableTypes,
      hasNonZeroStats,
      formatDropChance,
      handleImageError,
      showGhostDetails,
      showBossGhostDetails,
      showMerchantDetails,
      showItemDetails,
      user,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
@keyframes tabSlide { from { width: 0; } to { width: 100%; } }
.animate-tabSlide { animation: tabSlide 0.2s ease-out; }
@keyframes spinPulse {
  0% { transform: rotate(0deg); opacity: 0.7; }
  50% { opacity: 1; }
  100% { transform: rotate(360deg); opacity: 0.7; }
}
.animate-spin-pulse {
  animation: spinPulse 1s linear infinite;
  border-right-color: transparent;
  border-bottom-color: transparent;
}

/* Transition for <transition name="fade"> */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Tooltip */
.tooltip {
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #e0e7ff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 70;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin-bottom: 0.5rem;
}
button:hover .tooltip { visibility: visible; opacity: 1; }

/* Mobile */
@media (max-width: 639px) {
  .text-sm { font-size: 0.75rem; }
  .text-base { font-size: 0.875rem; }
  .p-4 { padding: 0.75rem; }
  .max-h-96 { max-height: 16rem; }
  button[aria-label="ปิดหน้าต่างรายละเอียดไอเทม"],
  button[aria-label="ปิด"] {
    padding: 0.75rem;
    margin: -0.5rem;
    touch-action: manipulation;
  }
  button[aria-label="ปิดหน้าต่างรายละเอียดไอเทม"] i,
  button[aria-label="ปิด"] i { font-size: 1.25rem; }
  .z-60 { max-width: 90%; margin: 0 auto; }
}

/* Extra tiny text utility */
.text-xxs { font-size: 0.7rem; line-height: 1rem; }

/* Basic Info & Fee */
.bi-card { background: #1f2937; border: 1px solid #374151; border-radius: 8px; padding: 16px; color: #c7d2fe; }
.bi-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.bi-title { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; color: #a5b4fc; }
.bi-title i { color: #818cf8; }
.bi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (min-width: 640px) { .bi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 768px) { .bi-grid { grid-template-columns: repeat(3, 1fr); } }
.bi-item { display: flex; align-items: center; gap: 8px; background: rgba(17,24,39,0.5); border: 1px solid #374151; border-radius: 8px; padding: 10px; font-size: 12px; color: #e0e7ff; }
.bi-item i { font-size: 12px; color: #a5b4fc; }
.bi-span2 { grid-column: 1 / -1; }
.fee-card { background: #1f2937; border: 1px solid #374151; border-radius: 8px; padding: 16px; color: #c7d2fe; }
.fee-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.fee-title { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; color: #a5b4fc; }
.fee-title i { color: #818cf8; }
.fee-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (min-width: 640px) { .fee-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 768px) { .fee-grid { grid-template-columns: repeat(4, 1fr); } }
.fee-chip { display: flex; align-items: center; justify-content: center; gap: 6px; background: #374151; border: 1px solid #4b5563; border-radius: 8px; padding: 8px 10px; font-size: 12px; color: #e0e7ff; transition: transform .12s ease, background .15s ease, border-color .15s ease; }
.fee-chip:hover { transform: translateY(-1px); background: #3f4758; border-color: #5b6473; }
.fee-chip i { font-size: 12px; }
.fee-coin { color: #fbbf24; }
.fee-gold { color: #f59e0b; }
.fee-diamond { color: #93c5fd; }
.fee-cash { color: #86efac; }
</style>